import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {makeStyles} from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Card from '@material-ui/core/Card';
import CardHeader from '@material-ui/core/CardHeader';
import Divider from '@material-ui/core/Divider';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Checkbox from '@material-ui/core/Checkbox';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import {documentosDisponiveis} from '../../components/processo/processoSelecao.js';
import {outlinedButton} from '../../../../utils/styles.js';

const useStyles = makeStyles(theme => ({
    cardHeader: {
        padding: theme.spacing(1, 2)
    },
    list: {
        height: 320,
        overflow: 'auto',
        [theme.breakpoints.down('xs')]: {
            height: 220
        }
    },
    vazio: {
        padding: theme.spacing(2),
        color: theme.palette.text.secondary
    },
    botoes: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
        [theme.breakpoints.down('xs')]: {
            flexDirection: 'row',
            justifyContent: 'center'
        }
    },
    ...outlinedButton(theme, false, false)
}));

const mesmoId = (a, b) => String(a?.id) === String(b?.id);
const contem = (lista, item) => lista.some(atual => mesmoId(atual, item));
const sem = (lista, remover) => lista.filter(item => !contem(remover, item));
const em = (lista, manter) => lista.filter(item => contem(manter, item));

/*
 * Aba "Documentos": os documentos exigidos no processo, numa lista dupla
 * Disponíveis × Selecionados (o pickList do PrimeFaces), no estilo do
 * Transfer List do MUI v4. O rótulo do item é o nome do documento.
 */
export default function TabDocumentos({watch, setValue, documentos = []}) {
    const classes = useStyles();
    const [marcados, setMarcados] = useState([]);

    const selecionados = watch('documentos') || [];
    const disponiveis = documentosDisponiveis(documentos, selecionados);
    const marcadosDisponiveis = em(marcados, disponiveis);
    const marcadosSelecionados = em(marcados, selecionados);

    const gravar = lista => setValue('documentos', lista.map(item => ({id: item.id, nome: item.nome})), {shouldDirty: true});

    const alternar = item => () => setMarcados(contem(marcados, item) ? sem(marcados, [item]) : [...marcados, item]);

    const marcarTodos = itens => () => {
        const todosMarcados = itens.length > 0 && em(marcados, itens).length === itens.length;
        setMarcados(todosMarcados ? sem(marcados, itens) : [...sem(marcados, itens), ...itens]);
    };

    const moverTodosParaSelecionados = () => {
        gravar([...selecionados, ...disponiveis]);
        setMarcados([]);
    };

    const moverMarcadosParaSelecionados = () => {
        gravar([...selecionados, ...marcadosDisponiveis]);
        setMarcados(sem(marcados, marcadosDisponiveis));
    };

    const moverMarcadosParaDisponiveis = () => {
        gravar(sem(selecionados, marcadosSelecionados));
        setMarcados(sem(marcados, marcadosSelecionados));
    };

    const moverTodosParaDisponiveis = () => {
        gravar([]);
        setMarcados([]);
    };

    const lista = (titulo, itens, vazio) => {
        const quantidadeMarcada = em(marcados, itens).length;
        return (
            <Card variant='outlined'>
                <CardHeader
                    className={classes.cardHeader}
                    avatar={(
                        <Checkbox
                            color='primary'
                            onClick={marcarTodos(itens)}
                            checked={itens.length > 0 && quantidadeMarcada === itens.length}
                            indeterminate={quantidadeMarcada > 0 && quantidadeMarcada < itens.length}
                            disabled={itens.length === 0}
                            inputProps={{'aria-label': `Marcar todos: ${titulo}`}}/>
                    )}
                    title={titulo}
                    subheader={`${quantidadeMarcada}/${itens.length} marcados`}/>
                <Divider/>
                <List dense component='div' role='list' className={classes.list}>
                    {itens.length === 0 && <Typography variant='body2' className={classes.vazio}>{vazio}</Typography>}
                    {itens.map(item => (
                        <ListItem key={item.id} role='listitem' button onClick={alternar(item)}>
                            <ListItemIcon>
                                <Checkbox color='primary' checked={contem(marcados, item)} tabIndex={-1} disableRipple
                                          inputProps={{'aria-labelledby': `documento-processo-${item.id}`}}/>
                            </ListItemIcon>
                            <ListItemText id={`documento-processo-${item.id}`} primary={item.nome}/>
                        </ListItem>
                    ))}
                </List>
            </Card>
        );
    };

    const botao = (rotulo, titulo, onClick, disabled) => (
        <Button variant='outlined' size='small' className={classes.outlinedButton} onClick={onClick} disabled={disabled}
                aria-label={titulo} title={titulo}>
            {rotulo}
        </Button>
    );

    return (
        <Grid container spacing={3} alignItems='center'>
            <Grid item xs={12} sm={5}>
                {lista('Disponíveis', disponiveis, 'Nenhum documento disponível.')}
            </Grid>
            <Grid item xs={12} sm={2} className={classes.botoes}>
                {botao('≫', 'Selecionar todos', moverTodosParaSelecionados, disponiveis.length === 0)}
                {botao('>', 'Selecionar marcados', moverMarcadosParaSelecionados, marcadosDisponiveis.length === 0)}
                {botao('<', 'Remover marcados', moverMarcadosParaDisponiveis, marcadosSelecionados.length === 0)}
                {botao('≪', 'Remover todos', moverTodosParaDisponiveis, selecionados.length === 0)}
            </Grid>
            <Grid item xs={12} sm={5}>
                {lista('Selecionados', selecionados, 'Nenhum documento selecionado.')}
            </Grid>
        </Grid>
    );
}

TabDocumentos.propTypes = {
    watch: PropTypes.func.isRequired,
    setValue: PropTypes.func.isRequired,
    /* Documentos do apoio: [{id, ordem, nome}]. */
    documentos: PropTypes.array
};
